/* ═══════════════════════════════════════════════════════════════
   The Designer — Word-grade tables (contract §4 R7).

   SPEC cut-list (written first, per the R7 brief):

   IN SCOPE
     · Real TipTap/ProseMirror table nodes — @tiptap/extension-table(+
       -row/-header/-cell), NOT the old atom/contentEditable-div table.
       Kept at their OFFICIAL node names (table/tableRow/tableHeader/
       tableCell): prosemirror-tables and @tiptap/extension-table hardcode
       those names in a couple of internal spots (TableRow's own content
       expression, the "select-all-cells then Backspace" shortcut), so
       renaming them would silently break those paths. Nothing else in
       the framework cares about the name — every command/plugin resolves
       nodes generically via `tableRole`, which our attrs still carry.
     · One new node, `specCellParagraph`, is the ONLY editable text
       container inside a cell — same three marks (bold/italic/underline)
       plus highlight as everywhere else in the Designer, no font/size/
       colour path, matching schema.ts's founding rule.
     · Table-or-paragraph placement: `numbered` boolean attr on the table
       node. Numbered → the table carries the level's MasterFormat number
       (docx: an anchor paragraph with the number and no text, table
       indented to that level's text indent, immediately after).
       Standalone (default) → sits at the margin between paragraphs, no
       number, exactly like every table that existed before this field.
       Switchable after insertion via the table toolbar.
     · Row/column insert/delete, resize (framework's columnResizing),
       merge/split cells, toggle header row (repeats on export), per-cell
       alignment — all the FRAMEWORK's own commands (insertTable /
       addColumnBefore / addColumnAfter / deleteColumn / addRowBefore /
       addRowAfter / deleteRow / deleteTable / mergeCells / splitCell /
       toggleHeaderRow / setCellAttribute). This module supplies zero
       hand-rolled editing logic — only attrs, conversion, and a NodeView
       whose sole job is visual (colgroup %, border/shading CSS vars).
     · TableData round-trip: `TableCell` gains `runs`/`highlight` (mirrors
       ContentBlock exactly — same fold/unfold math, shared/text-runs.ts);
       `TableData` gains `numbered`. Everything else (colWidths, borders,
       header shading, justify) rides as the table node's `tableStyle`
       attr, re-derived into TableData at every save.
     · Cells accept `specCellParagraph+` (one OR MORE), not exactly one:
       the framework's own `mergeCells` command concatenates non-empty
       cells' content into the surviving cell, and "exactly one paragraph"
       would make that produce a schema-invalid document. Extra paragraphs
       therefore round-trip (joined with `\n`, matching how the harvester
       already joins multi-paragraph Word cells) but are NOT a designed
       authoring feature — no "add paragraph" UI, Enter inside a cell just
       does whatever the framework's default split does.

   OUT OF SCOPE (cut, not silently dropped — matches the R7 brief verbatim)
     · Nested tables, per-cell fonts/sizes, a table-style gallery, vertical
       merge UI beyond the framework's own rowSpan merge/split, cell
       margins UI, autofit modes.
     · A forma-level "table style" source of truth does not exist yet
       (grepped: no such field on StandardConfig). Borders/shading stay
       PER-TABLE, exactly as they already were pre-R7 (BlockEditor's table
       properties panel, the harvester's imported defaults) — flagged in
       the R7 report as a gap to close in a future forma-styles pass, not
       solved here.
     · The framework's Backspace/Delete "delete table when every cell is
       selected" shortcut is hardcoded to the default node names and will
       not fire under any renamed schema — moot here since we kept the
       default names, but noted because the OFFICIAL "Delete table" command
       (wired to our toolbar button) is the one this build guarantees.
   ═══════════════════════════════════════════════════════════════ */

import { Node as TipTapNode, mergeAttributes } from '@tiptap/core';
import type { JSONContent } from '@tiptap/core';
import Table, { createTable } from '@tiptap/extension-table';
import type { TableOptions } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import type { Node as PMNode, Schema } from '@tiptap/pm/model';
import type { EditorView, NodeView } from '@tiptap/pm/view';
import type { ContentBlock, TableCell as TableCellData, TableData } from '../../shared/types';
import {
  effectiveRunsGeneric,
  foldRunsGeneric,
  nodeRunsFromJSON,
  type MarkFlags,
} from '../../shared/text-runs';
import { blockAttrs, newBlockId, type BlockMeta } from './nodeBase';

/** The table node's own name — kept at the framework default (see cut-list
 *  above). Exported so the rest of the Designer never re-types the string. */
export const TABLE_NODE_NAME = 'table';

// ─── Table-level style (everything a TableData carries EXCEPT the grid
//     itself — rows/cols/cells/headerRows are always derived from the live
//     node tree, never trusted from a stale attr). ───

export interface TableStyleAttrs {
  colWidths?: number[];
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: 'solid' | 'dashed' | 'dotted' | 'double' | 'none';
  innerBorderWidth?: number;
  innerBorderColor?: string;
  innerBorderStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
  headerBgColor?: string;
  headerBgOpacity?: number;
  tableJustify?: 'left' | 'center' | 'right';
}

const TABLE_STYLE_KEYS = [
  'colWidths',
  'borderWidth',
  'borderColor',
  'borderStyle',
  'innerBorderWidth',
  'innerBorderColor',
  'innerBorderStyle',
  'headerBgColor',
  'headerBgOpacity',
  'tableJustify',
] as const;

/** The same starter look BlockEditor and the old Designer atom gave a new
 *  table, so a table born here prints identically to one born anywhere
 *  else. These hex values are DOCUMENT data (what the .docx borders print
 *  as), not UI chrome. */
export const DEFAULT_NEW_TABLE_STYLE: TableStyleAttrs = {
  borderWidth: 1,
  borderColor: '000000',
  borderStyle: 'solid',
  innerBorderWidth: 0.5,
  innerBorderColor: '999999',
  innerBorderStyle: 'solid',
  headerBgColor: 'E8E8E8',
  headerBgOpacity: 100,
  tableJustify: 'left',
};

// ─── The table's NodeView — VISUAL ONLY. contentDOM is a plain <tbody>;
//     every keystroke, selection and structural edit stays ProseMirror's.
//     The only thing this class does the framework's own TableView (which
//     it closely mirrors) doesn't is (a) fall back to `tableStyle.colWidths`
//     percentages for columns the user has never dragged, and (b) paint the
//     table's border/shading/justify from `tableStyle` via CSS variables.
//     Defined BEFORE `SpecTable` below: `Table.extend()` calls `addOptions()`
//     (which references this class) eagerly, at module-evaluation time. ───

function borderCss(
  widthPt: number | undefined,
  style: string | undefined,
  color: string | undefined
): string {
  if (!widthPt || widthPt <= 0 || style === 'none') return 'none';
  return `${widthPt}pt ${style || 'solid'} #${(color || '000000').replace('#', '')}`;
}

function blendToWhite(hex: string, opacityPct?: number): string {
  const clean = (hex || '').replace('#', '').padEnd(6, '0').slice(0, 6);
  const o = Math.max(0, Math.min(100, opacityPct ?? 100)) / 100;
  if (o >= 1) return `#${clean}`;
  let out = '';
  for (let i = 0; i < 3; i++) {
    const c = parseInt(clean.slice(i * 2, i * 2 + 2), 16) || 0;
    out += Math.round(c * o + 255 * (1 - o))
      .toString(16)
      .padStart(2, '0');
  }
  return `#${out}`;
}

export class SpecTableView implements NodeView {
  dom: HTMLElement;
  contentDOM: HTMLElement;
  private node: PMNode;
  private cellMinWidth: number;
  private table: HTMLTableElement;
  private colgroup: HTMLElement;

  constructor(node: PMNode, cellMinWidth: number, _view: EditorView) {
    this.node = node;
    this.cellMinWidth = cellMinWidth || 40;
    this.dom = document.createElement('div');
    this.dom.className = 'tableWrapper dsg-real-table-wrap';
    this.table = this.dom.appendChild(document.createElement('table'));
    this.table.className = 'dsg-real-table';
    this.colgroup = this.table.appendChild(document.createElement('colgroup'));
    this.contentDOM = this.table.appendChild(document.createElement('tbody'));
    this.render();
  }

  private render() {
    this.applyStyleVars(this.node);
    this.updateColgroup(this.node);
  }

  private updateColgroup(node: PMNode) {
    this.colgroup.innerHTML = '';
    const row = node.firstChild;
    if (!row) return;
    const pct = (node.attrs.tableStyle as TableStyleAttrs | undefined)?.colWidths;
    let anyLive = false;
    row.forEach((cell) => {
      const colwidth = cell.attrs.colwidth as number[] | null;
      if (colwidth && colwidth.some((w) => w)) anyLive = true;
    });
    let col = 0;
    row.forEach((cell) => {
      const colspan = (cell.attrs.colspan as number) || 1;
      const colwidth = cell.attrs.colwidth as number[] | null;
      for (let j = 0; j < colspan; j++, col++) {
        const colEl = document.createElement('col');
        const live = colwidth && colwidth[j];
        if (live) colEl.style.width = `${live}px`;
        else if (pct && pct[col]) colEl.style.width = `${pct[col]}%`;
        else colEl.style.minWidth = `${this.cellMinWidth}px`;
        this.colgroup.appendChild(colEl);
      }
    });
    this.table.style.tableLayout = anyLive || pct ? 'fixed' : 'auto';
    this.table.style.width = '100%';
  }

  private applyStyleVars(node: PMNode) {
    const style = (node.attrs.tableStyle || {}) as TableStyleAttrs;
    const outer = borderCss(
      style.borderWidth ?? 1,
      style.borderStyle || 'solid',
      style.borderColor || '000000'
    );
    const inner = borderCss(
      style.innerBorderWidth ?? 0.5,
      style.innerBorderStyle || 'solid',
      style.innerBorderColor || '999999'
    );
    this.table.style.setProperty('--dsg-tbl-inner', inner);
    this.table.style.setProperty(
      '--dsg-tbl-header-bg',
      style.headerBgColor
        ? blendToWhite(style.headerBgColor, style.headerBgOpacity ?? 100)
        : 'transparent'
    );
    this.table.style.border = outer;
    this.table.classList.toggle('dsg-tbl-numbered', !!node.attrs.numbered);
    this.dom.style.margin =
      style.tableJustify === 'center'
        ? '0 auto'
        : style.tableJustify === 'right'
          ? '0 0 0 auto'
          : '0';
  }

  update(node: PMNode) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  ignoreMutation(mutation: { type: string; target: Node }) {
    return (
      mutation.type === 'attributes' &&
      (mutation.target === this.table || this.colgroup.contains(mutation.target))
    );
  }
}

// ─── Nodes ───

/** The ONLY editable text container inside a cell. Same three marks +
 *  highlight as specParagraph — no font/size/colour path here either. */
export const SpecCellParagraph = TipTapNode.create({
  name: 'specCellParagraph',
  group: 'block',
  content: 'inline*',
  parseHTML: () => [{ tag: 'p[data-cell-p]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'p',
    mergeAttributes(HTMLAttributes, { 'data-cell-p': '' }),
    0,
  ],
});

/** Style attrs every cell (data or header) carries on top of the framework's
 *  own colspan/rowspan/colwidth. */
const CELL_STYLE_ATTRS = {
  align: { default: null as 'left' | 'center' | 'right' | null },
  bgColor: { default: null as string | null },
  bgOpacity: { default: null as number | null },
};

function cellHtmlAttrs(node: PMNode): Record<string, string> {
  const align = node.attrs.align as string | null;
  const bgColor = node.attrs.bgColor as string | null;
  const bgOpacity = node.attrs.bgOpacity as number | null;
  const style: string[] = [];
  if (align) style.push(`text-align:${align}`);
  if (bgColor) style.push(`background:${blendToWhite(bgColor, bgOpacity ?? 100)}`);
  return style.length ? { style: style.join(';') } : {};
}

export const SpecTableCell = TableCell.extend({
  content: 'specCellParagraph+',
  addAttributes() {
    return { ...this.parent?.(), ...CELL_STYLE_ATTRS };
  },
  renderHTML({ HTMLAttributes, node }) {
    return ['td', mergeAttributes(HTMLAttributes, cellHtmlAttrs(node)), 0];
  },
});

export const SpecTableHeader = TableHeader.extend({
  content: 'specCellParagraph+',
  addAttributes() {
    return { ...this.parent?.(), ...CELL_STYLE_ATTRS };
  },
  renderHTML({ HTMLAttributes, node }) {
    return ['th', mergeAttributes(HTMLAttributes, cellHtmlAttrs(node)), 0];
  },
});

export const SpecTable = Table.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      resizable: true,
      cellMinWidth: 40,
      handleWidth: 6,
      lastColumnResizable: true,
      View: SpecTableView as unknown as TableOptions['View'],
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      ...blockAttrs,
      text: { default: '' },
      /** §4 R7: numbered-under-level vs standalone. */
      numbered: { default: false },
      tableStyle: { default: {} as TableStyleAttrs },
      /** Forward-compat catch-all, mirrors schema.ts's paragraph `meta` —
       *  any TableData field this module doesn't know about yet rides here
       *  untouched instead of being silently dropped. */
      tableMeta: { default: {} as BlockMeta },
    };
  },
});

/** Every extension the table feature needs, in DESIGNER_NODES order. */
export const TABLE_NODES = [SpecTable, TableRow, SpecTableHeader, SpecTableCell, SpecCellParagraph];

// ─── ContentBlock (tableData) → ProseMirror JSON ───

function marksForRun(flags: MarkFlags): JSONContent['marks'] {
  const marks: NonNullable<JSONContent['marks']> = [];
  if (flags.bold) marks.push({ type: 'bold' });
  if (flags.italic) marks.push({ type: 'italic' });
  if (flags.underline) marks.push({ type: 'underline' });
  if (flags.highlight) marks.push({ type: 'highlight' });
  return marks.length ? marks : undefined;
}

function cellToNodeJSON(cell: TableCellData, isHeader: boolean): JSONContent {
  const runs = effectiveRunsGeneric(cell);
  return {
    type: isHeader ? 'tableHeader' : 'tableCell',
    attrs: {
      colspan: Math.max(1, cell.colSpan || 1),
      rowspan: Math.max(1, cell.rowSpan || 1),
      colwidth: null,
      align: cell.align || null,
      bgColor: cell.bgColor || null,
      bgOpacity: cell.bgOpacity ?? null,
    },
    content: [
      {
        type: 'specCellParagraph',
        ...(runs.length
          ? {
              content: runs.map((r) => ({
                type: 'text',
                text: r.text,
                ...(marksForRun(r) ? { marks: marksForRun(r) } : {}),
              })),
            }
          : {}),
      },
    ],
  };
}

/** Build the full `table` JSONContent node for a `blockType: 'table'`
 *  ContentBlock — the table half of schema.ts's `blockToNode`. */
export function tableDataToNodeJSON(
  b: ContentBlock,
  baseAttrs: {
    blockId: string;
    level: number;
    continuation: boolean;
    excluded: boolean;
    meta: BlockMeta;
  }
): JSONContent {
  const td = b.tableData;
  const rows = td?.cells?.length ? td.cells : [[{ text: '' }]];
  const style: TableStyleAttrs = {};
  for (const k of TABLE_STYLE_KEYS) {
    const v = (td as unknown as Record<string, unknown> | undefined)?.[k];
    if (v !== undefined) (style as Record<string, unknown>)[k] = v;
  }
  const headerRows = td?.headerRows || 0;
  return {
    type: TABLE_NODE_NAME,
    attrs: {
      ...baseAttrs,
      text: b.text || '',
      numbered: !!td?.numbered,
      tableStyle: style,
      tableMeta: {},
    },
    content: rows.map((row, ri) => ({
      type: 'tableRow',
      content: row.map((cell) => cellToNodeJSON(cell, ri < headerRows)),
    })),
  };
}

// ─── ProseMirror JSON → TableData ───

function cellFromNodeJSON(c: JSONContent): TableCellData {
  const paras = (c.content || []).filter((n) => n.type === 'specCellParagraph');
  const flatRuns: Array<{ text: string } & MarkFlags> = [];
  paras.forEach((p, i) => {
    if (i > 0)
      flatRuns.push({ text: '\n', bold: false, italic: false, underline: false, highlight: false });
    flatRuns.push(...nodeRunsFromJSON(p.content));
  });
  const folded = foldRunsGeneric(flatRuns);
  const attrs = (c.attrs || {}) as Record<string, unknown>;
  const colSpan = Math.max(1, (attrs.colspan as number) || 1);
  const rowSpan = Math.max(1, (attrs.rowspan as number) || 1);
  return {
    text: folded.text,
    ...(folded.bold ? { bold: true } : {}),
    ...(folded.italic ? { italic: true } : {}),
    ...(folded.underline ? { underline: true } : {}),
    ...(folded.highlight ? { highlight: true } : {}),
    ...(folded.runs ? { runs: folded.runs } : {}),
    ...(attrs.align ? { align: attrs.align as TableCellData['align'] } : {}),
    ...(attrs.bgColor ? { bgColor: attrs.bgColor as string } : {}),
    ...(attrs.bgOpacity !== null && attrs.bgOpacity !== undefined
      ? { bgOpacity: attrs.bgOpacity as number }
      : {}),
    ...(colSpan > 1 ? { colSpan } : {}),
    ...(rowSpan > 1 ? { rowSpan } : {}),
  };
}

/** A row counts as a header row when EVERY cell in it is a `tableHeader` —
 *  exactly what the framework's own `toggleHeaderRow` command produces. */
function countLeadingHeaderRows(rows: JSONContent[]): number {
  let n = 0;
  for (const row of rows) {
    const cells = row.content || [];
    if (cells.length && cells.every((c) => c.type === 'tableHeader')) n++;
    else break;
  }
  return n;
}

/** Column widths, live from the framework's own resizer when a resize has
 *  actually happened (any cell in row 0 carries a non-null `colwidth`); the
 *  stored/authored percentages otherwise — so a freshly loaded, never-
 *  resized table round-trips its `colWidths` byte-identical. */
function deriveLiveColWidths(rows: JSONContent[], gridCols: number): number[] | undefined {
  const first = rows[0];
  if (!first || gridCols <= 0) return undefined;
  const cells = first.content || [];
  const px: number[] = new Array(gridCols).fill(0);
  let any = false;
  let col = 0;
  for (const c of cells) {
    const attrs = (c.attrs || {}) as Record<string, unknown>;
    const span = Math.max(1, (attrs.colspan as number) || 1);
    const cw = (attrs.colwidth as number[] | null) || null;
    for (let j = 0; j < span; j++, col++) {
      const w = cw && cw[j] ? cw[j] : 0;
      if (w) any = true;
      if (col < gridCols) px[col] = w;
    }
  }
  if (!any) return undefined;
  const known = px.reduce((s, w) => s + w, 0);
  const unset = px.filter((w) => !w).length;
  const fallback = unset ? Math.max(20, known / Math.max(1, gridCols - unset)) : 0;
  const filled = px.map((w) => w || fallback);
  const total = filled.reduce((s, w) => s + w, 0) || 1;
  return filled.map((w) => Math.round((w / total) * 10000) / 100);
}

/** Build the `TableData` half of schema.ts's `nodeToBlock` for a `table`
 *  JSONContent node — the grid (rows/cols/cells/headerRows) is always
 *  derived fresh from the live content; style rides through `tableStyle`. */
export function nodeToTableData(node: JSONContent): TableData {
  const rows = node.content || [];
  const style = (node.attrs?.tableStyle || {}) as TableStyleAttrs;
  const cells: TableCellData[][] = rows.map((row) => (row.content || []).map(cellFromNodeJSON));
  const headerRows = countLeadingHeaderRows(rows);
  const gridCols = rows.reduce((max, row) => {
    const sum = (row.content || []).reduce((s, c) => {
      const attrs = (c.attrs || {}) as Record<string, unknown>;
      return s + Math.max(1, (attrs.colspan as number) || 1);
    }, 0);
    return Math.max(max, sum);
  }, 0);
  const liveColWidths = deriveLiveColWidths(rows, gridCols);
  const colWidths = liveColWidths ?? style.colWidths;

  const out: TableData = {
    rows: rows.length,
    cols: gridCols || (cells[0]?.length ?? 1),
    cells,
  };
  if (colWidths !== undefined) out.colWidths = colWidths;
  if (style.borderWidth !== undefined) out.borderWidth = style.borderWidth;
  if (style.borderColor !== undefined) out.borderColor = style.borderColor;
  if (style.borderStyle !== undefined)
    out.borderStyle = style.borderStyle as TableData['borderStyle'];
  if (style.innerBorderWidth !== undefined) out.innerBorderWidth = style.innerBorderWidth;
  if (style.innerBorderColor !== undefined) out.innerBorderColor = style.innerBorderColor;
  if (style.innerBorderStyle !== undefined)
    out.innerBorderStyle = style.innerBorderStyle as TableData['innerBorderStyle'];
  if (style.headerBgColor !== undefined) out.headerBgColor = style.headerBgColor;
  if (style.headerBgOpacity !== undefined) out.headerBgOpacity = style.headerBgOpacity;
  if (style.tableJustify !== undefined) out.tableJustify = style.tableJustify;
  if (headerRows > 0) out.headerRows = headerRows;
  if (node.attrs?.numbered) out.numbered = true;
  return out;
}

// ─── Insertion ───

/** A fresh 3×3 table with a header row, at the given level — built entirely
 *  from the framework's own `createTable` utility (nothing hand-rolled),
 *  with our block identity + default style layered on afterward. */
export function buildTableNode(schema: Schema, level: number): PMNode {
  const base = createTable(schema, 3, 3, true);
  return base.type.create(
    {
      blockId: newBlockId(),
      level,
      continuation: false,
      excluded: false,
      meta: {},
      text: '',
      numbered: false,
      tableStyle: { ...DEFAULT_NEW_TABLE_STYLE, colWidths: [33, 33, 34] },
      tableMeta: {},
    },
    base.content,
    base.marks
  );
}
